import { inject, Injectable } from "@angular/core";

import { LogService } from "@bitwarden/common/abstractions/log.service";
import { DEFAULT_CLASSIFICATION_ID } from "@bitwarden/web-vault/app/models/constants/classification.constants";
import { GLOSS_CREDITS_ACCOUNT_NAME } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { AccountStoreModel } from "@bitwarden/web-vault/app/models/store/account.store.model";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { SplitClassificationType } from "@bitwarden/web-vault/app/models/types/split-classification-type";
import {
  AllowedLocations,
  DefaultSystemLocation,
  UserLocationType,
} from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { GlossCreditsUserService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/gloss-credits.user";
import {
  getGlossVaultInstitution,
  getGlossVaultInstitutionAccountId,
  getGlossVaultInstitutionId,
} from "@bitwarden/web-vault/app/shared/utils/helper.institution/gloss-vault-institution.helper";

@Injectable({
  providedIn: "root",
})
export class GlossCreditsAccountService {
  private userStore: UserStoreService = inject(UserStoreService);
  private log: LogService = inject(LogService);
  private glossCreditsUserService: GlossCreditsUserService = inject(GlossCreditsUserService);

  /**
   * Ensures the Gloss Credits account exists. Creates it if not found.
   * Called on login after vault is loaded.
   * @returns The Gloss Credits account if it exists or was created, null otherwise.
   */
  async ensureGlossCreditsAccountExists(): Promise<AccountView | null> {
    try {
      const existing = this.findGlossCreditsAccount();
      if (existing) {
        return existing;
      }

      const account = await this.createGlossCreditsAccount();
      return account;
    } catch (error) {
      this.log.error(
        `GlossCreditsAccountService: Failed to ensure Gloss Credits account: ${error?.message || error}`,
      );
      return null;
    }
  }

  findGlossCreditsAccount(): AccountView | null {
    const accounts = this.userStore.accounts.accountViews();
    if (!accounts) {
      return null;
    }
    return accounts.find((a) => a?.name === GLOSS_CREDITS_ACCOUNT_NAME) ?? null;
  }

  private async ensureGlossVaultInstitutionInVault(countryCode: string): Promise<void> {
    const institutionId = getGlossVaultInstitutionId(countryCode);
    const institutions = this.userStore.institutions.institutionViews() ?? [];
    const existing = institutions.find((inst) => inst.id === institutionId);
    if (existing) {
      return;
    }
    const glossVaultInstitution = getGlossVaultInstitution(countryCode);
    await this.userStore.institutions.saveToVault([glossVaultInstitution], true);
  }

  private getGlossVaultInstitutionIds(): {
    institutionId: string;
    institutionAccountId: string;
    countryCode: string;
  } {
    const location =
      (this.userStore.preferences.preferenceView()?.userLocation as UserLocationType) ?? null;
    const countryCode =
      location && (AllowedLocations as readonly string[]).includes(location)
        ? location
        : DefaultSystemLocation;

    const institutionId = getGlossVaultInstitutionId(countryCode);
    const institutionAccountId = getGlossVaultInstitutionAccountId(institutionId);

    return { institutionId, institutionAccountId, countryCode };
  }

  private async createGlossCreditsAccount(): Promise<AccountView> {
    await this.glossCreditsUserService.registerUserToGlossBanking();

    const { institutionId, institutionAccountId, countryCode } = this.getGlossVaultInstitutionIds();

    await this.ensureGlossVaultInstitutionInVault(countryCode);

    const defaultClassifications: SplitClassificationType[] = [
      {
        classificationId: DEFAULT_CLASSIFICATION_ID,
        weight: 1,
        roundingDefault: false,
      },
    ];

    const account = new AccountView();
    account.name = GLOSS_CREDITS_ACCOUNT_NAME;
    account.currency = "AUD";
    account.institutionLink = { institutionId, institutionAccountId };
    account.origin = Origin.system;
    account.type = "other";
    account.defaultClassifications = defaultClassifications;
    account.defaultCategories = [];
    account.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    account.balance = 0;
    account.creditLimit = 0;
    account.basiqAccountId = "";
    account.syncStatusLink = [];

    const savedModel = await this.userStore.accounts.save(account);
    return new AccountView(savedModel as AccountStoreModel);
  }
}
